import { createAction, createSlice } from "@reduxjs/toolkit";
import { AdminNotification } from "@/types/notification.definition";

interface NotificationState {
  notifications: AdminNotification[];
  /** Only the list fetch flips this — a mark-read or a delete must not blank the panel. */
  loading: boolean;
  error: string | null;
  /** False until the first fetch settles, so an empty list is not mistaken for "not loaded yet". */
  loaded: boolean;
}

const initialState: NotificationState = {
  notifications: [],
  loading: false,
  error: null,
  loaded: false,
};

export const fetchNotificationsRequest = createAction(
  "notifications/fetchRequest"
);
export const fetchNotificationsSuccess = createAction<AdminNotification[]>(
  "notifications/fetchSuccess"
);
export const fetchNotificationsFailure = createAction<string>(
  "notifications/fetchFailure"
);

export const markNotificationReadRequest = createAction<{ id: number }>(
  "notifications/markReadRequest"
);
export const markNotificationReadSuccess = createAction<number>(
  "notifications/markReadSuccess"
);
export const markNotificationReadFailure = createAction<string>(
  "notifications/markReadFailure"
);

export const markAllNotificationsReadRequest = createAction(
  "notifications/markAllReadRequest"
);
export const markAllNotificationsReadSuccess = createAction(
  "notifications/markAllReadSuccess"
);
export const markAllNotificationsReadFailure = createAction<string>(
  "notifications/markAllReadFailure"
);

export const deleteNotificationRequest = createAction<{ id: number }>(
  "notifications/deleteRequest"
);
export const deleteNotificationSuccess = createAction<number>(
  "notifications/deleteSuccess"
);
export const deleteNotificationFailure = createAction<string>(
  "notifications/deleteFailure"
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearNotificationsError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchNotificationsRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationsSuccess, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.notifications = action.payload;
        state.error = null;
      })
      .addCase(fetchNotificationsFailure, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload;
      });

    builder
      .addCase(markNotificationReadSuccess, (state, action) => {
        state.notifications = state.notifications.map(notification =>
          notification.id === action.payload
            ? { ...notification, isRead: true }
            : notification
        );
        state.error = null;
      })
      .addCase(markNotificationReadFailure, (state, action) => {
        state.error = action.payload;
      });

    builder
      .addCase(markAllNotificationsReadSuccess, state => {
        state.notifications = state.notifications.map(notification => ({
          ...notification,
          isRead: true,
        }));
        state.error = null;
      })
      .addCase(markAllNotificationsReadFailure, (state, action) => {
        state.error = action.payload;
      });

    builder
      .addCase(deleteNotificationSuccess, (state, action) => {
        state.notifications = state.notifications.filter(
          notification => notification.id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteNotificationFailure, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearNotificationsError } = notificationSlice.actions;

export const selectNotifications = (state: {
  notifications: NotificationState;
}) => state.notifications.notifications;

export const selectNotificationsLoading = (state: {
  notifications: NotificationState;
}) => state.notifications.loading;

export const selectNotificationsLoaded = (state: {
  notifications: NotificationState;
}) => state.notifications.loaded;

export const selectNotificationsError = (state: {
  notifications: NotificationState;
}) => state.notifications.error;

/**
 * The BFF has no unread-count endpoint, so the badge is derived from the list.
 */
export const selectUnreadNotificationCount = (state: {
  notifications: NotificationState;
}) => state.notifications.notifications.filter(n => !n.isRead).length;

export default notificationSlice.reducer;
